import { FC } from 'react';

import leapifyLogo from 'assets/images/Leapify-Logo.svg';
import { Formik } from 'formik';
import { FcGoogle } from 'react-icons/fc';
import { Link } from 'react-router-dom';
import { Checkbox, Input } from 'ui/components';
import { Button } from 'ui/components/common';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import { LOGIN_FORM, LOGIN_SCHEMA } from './schema';
import { AppleSignInButton } from '../components/common';

const FORM_INIT: LOGIN_FORM = {
  emailAddress: '',
  password: '',
  remember: false,
};

interface PROPS {
  onSubmitForm: (payload: LOGIN_FORM) => void;
  isLoading: boolean;
}

const LoginForm: FC<PROPS> = ({ onSubmitForm, isLoading }) => {
  const handleGoogleLogin = () => {
    window.location.href = `${import.meta.env.VITE_APP_END_POINT}/api/v1/auth/accounts/google`;
  };

  return (
    <>
      <img
        alt="Leapify Logo"
        className="w-[170px] mb-[50px] mx-auto"
        src={leapifyLogo}
      />
      <Formik
        initialValues={FORM_INIT}
        onSubmit={onSubmitForm}
        validationSchema={LOGIN_SCHEMA}
      >
        {({ values, errors, handleChange, setFieldValue, handleSubmit }) => (
          <form
            onSubmit={handleSubmit}
            className={cn(
              'w-full max-w-[490px]',
              'px-[31px] py-9',
              'shadow-custom rounded-[10px] bg-fill-tertiary',
            )}
          >
            <p className="text-xl font-semibold text-center mb-[30px]">
              Sign in to your account
            </p>
            <div className="space-y-5 mb-[27px]">
              <Input
                placeholder="Email Address"
                name="emailAddress"
                value={values.emailAddress}
                onChange={handleChange}
                required
                subText={errors?.emailAddress as string}
                size={SIZE.Large}
                textType={errors?.emailAddress ? 'danger' : 'default'}
              />
              <Input
                placeholder="Password"
                name="password"
                value={values.password}
                onChange={handleChange}
                type="password"
                subText={errors?.password as string}
                required
                size={SIZE.Large}
                textType={errors?.password ? 'danger' : 'default'}
              />
              <div className="flex items-center justify-between">
                <div className="flex gap-2.5">
                  <Checkbox
                    checked={values.remember}
                    onChange={(e) =>
                      setFieldValue('remember', e.target.checked)
                    }
                  />
                  <div className="flex flex-col">
                    <p className="text-xs">Remember</p>
                  </div>
                </div>
                <Link
                  className="text-sm font-semibold text-primary-base"
                  to="/forgot-password"
                >
                  Forgot password
                </Link>
              </div>
            </div>
            <Button
              label="LOGIN"
              size="md"
              type="submit"
              className="w-full mb-[15px] font-semibold"
              disabled={isLoading}
            />
            <Button
              onClick={handleGoogleLogin}
              label="SIGN IN WITH GOOGLE"
              className="w-full mb-2 font-semibold"
              intent="tertiary"
              size="md"
              icon={FcGoogle}
              left
              disabled={isLoading}
            />
            <AppleSignInButton isLoading={isLoading} />
            <p className="mt-4 text-sm text-center">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-base">
                Create an account
              </Link>
            </p>
          </form>
        )}
      </Formik>
      <div className="mt-2 text-xs text-secondary-light">
        v{import.meta.env.VITE_APP_VERSION}
      </div>
    </>
  );
};

export default LoginForm;
